import { logger } from '@automata/utils';
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import findKey from 'lodash/findKey';
import type { Edge } from 'reactflow';

import {
  createLabwareHelper,
  createTaskHelper,
  deleteAssociatedTaskDependencies,
  deleteEdgeHelper,
  deleteNodesHelper,
  deselectAllNodesEdgesHelper,
  getHashedTimeConstraintsByIdMemoized,
  graphologyGraph,
  rectifyTimeConstraintDirection,
  rectifyTimeConstraints,
  timeConstraintConnectionExists,
} from './helpers';
import { updateTaskParameters } from '../../features/workflow/workflowSlice';
import { loadExistingWorkflow } from '../../migration';
import type {
  GraphEdges,
  NodePosition,
  SelectionUpdate,
  TimeConstraintInstance,
} from '../../types';
import { mergeSelectionUpdate, parseHandlePairId } from '../../utils';
import uuid from '../../uuid';
import {
  createLabware,
  createNodesAndEdges,
  createTask,
  cutNodeToClipboard,
  deleteNodesEdges,
} from '../actions';
import { generateTimeConstraintId } from '../helpers';

// TODO(IL, 2024-03-15): represent these types in types.ts ??
export type NodePositionById = Record<string, NodePosition>;
export type TimeConstraintInstancesById = {
  [id: string]: TimeConstraintInstance;
};

/** A dependency from Task B to Task A means that B is dependent on A. */
export type TaskDependency = {
  id: string;
  fromTaskId: string;
  toTaskId: string;
};
export type TaskDependenciesById = {
  [id: string]: TaskDependency;
};

export interface GraphState {
  handlePairIdOrderByNode: { [taskId: string]: string[] }; // TODO(IL, 2024-04-04): nest this by node, and delete handlePairIds when handles are deleted (eg, remove all edges, or remove node)
  labwareDependencyEdges: GraphEdges;
  dataDependencyEdges: GraphEdges;
  labwareNodes: NodePositionById;
  labwareSlotMappingByTask: {
    [taskId: string]: { [labwareId: string]: number };
  };
  nodeIdToColorIndex: { [nodeId: string]: number };
  selectedEdges: string[];
  selectedNodes: string[];
  taskDependencies: TaskDependenciesById;
  taskNodes: NodePositionById;
  timeConstraints: TimeConstraintInstancesById;
}

const initialState: GraphState = {
  handlePairIdOrderByNode: {},
  labwareDependencyEdges: [],
  dataDependencyEdges: [],
  labwareNodes: {},
  labwareSlotMappingByTask: {},
  nodeIdToColorIndex: {},
  selectedEdges: [],
  selectedNodes: [],
  taskDependencies: {},
  taskNodes: {},
  timeConstraints: {},
};

export const graphSlice = createSlice({
  name: 'graph',
  initialState,
  reducers: {
    moveNodes: (
      state,
      action: PayloadAction<{ [id: string]: { position: NodePosition } }>
    ) => {
      for (const [id, { position }] of Object.entries(action.payload)) {
        if (id in state.labwareNodes) {
          state.labwareNodes[id] = position;
        } else if (id in state.taskNodes) {
          state.taskNodes[id] = position;
        } else {
          logger.error('cannot move node, no position info for id', id);
        }
      }
    },
    deselectNodesEdges: (state, _action: PayloadAction<void>) => {
      deselectAllNodesEdgesHelper(state);
    },
    createLabwareDependencyEdge: (
      state,
      action: PayloadAction<{
        deleteEdgeIds: string[];
        edge: Edge;
        labwareId?: string;
        targetSlot?: number;
      }>
    ) => {
      const { deleteEdgeIds, edge, labwareId, targetSlot } = action.payload;
      for (const id of deleteEdgeIds) {
        deleteEdgeHelper(state, id);
      }

      // TODO(IL, 2024-04-04): DRY the handlePairIdOrderByNode updates
      // Add target/source handlePairId's to handlePairIdOrderByNode.
      const targetHandlePairId =
        edge.targetHandle != null ? parseHandlePairId(edge.targetHandle) : null;
      if (targetHandlePairId !== null) {
        const handlePairOrder = state.handlePairIdOrderByNode[edge.target];

        if (handlePairOrder === undefined) {
          state.handlePairIdOrderByNode[edge.target] = [targetHandlePairId];
        } else if (!handlePairOrder.includes(targetHandlePairId)) {
          handlePairOrder.push(targetHandlePairId);
        }
      }
      const sourceHandlePairId =
        edge.sourceHandle != null ? parseHandlePairId(edge.sourceHandle) : null;
      if (sourceHandlePairId !== null) {
        const handlePairOrder = state.handlePairIdOrderByNode[edge.source];

        if (handlePairOrder === undefined) {
          state.handlePairIdOrderByNode[edge.source] = [sourceHandlePairId];
        } else if (!handlePairOrder.includes(sourceHandlePairId)) {
          handlePairOrder.push(sourceHandlePairId);
        }
      }

      state.labwareDependencyEdges.push(edge);

      // add new mapping to auto-assigned "next" slot
      // TODO(IL, 2024-04-08): remove this & the payload keys labwareId + targetSlot if the functionality isn't added soon
      const taskId = edge.target;
      const labwareMapping = state.labwareSlotMappingByTask[taskId];
      if (
        labwareMapping !== undefined &&
        labwareId !== undefined &&
        targetSlot !== undefined
      ) {
        labwareMapping[labwareId] = targetSlot;
      }

      state.timeConstraints = {
        ...state.timeConstraints,
        ...rectifyTimeConstraints(
          state.taskNodes,
          state.labwareNodes,
          state.timeConstraints,
          state.labwareDependencyEdges
        ),
      };
    },
    deleteEdges: (state, action: PayloadAction<string[]>) => {
      const toDelete = new Set(action.payload);
      for (const id of toDelete) {
        deleteEdgeHelper(state, id);
      }
      state.selectedEdges = state.selectedEdges.filter(
        (id) => !toDelete.has(id)
      );
      state.timeConstraints = {
        ...state.timeConstraints,
        ...rectifyTimeConstraints(
          state.taskNodes,
          state.labwareNodes,
          state.timeConstraints,
          state.labwareDependencyEdges
        ),
      };
    },
    createDataDependencyEdge: (
      state,
      action: PayloadAction<{
        deleteEdgeIds: string[];
        edge: Edge;
        dataId?: string;
      }>
    ) => {
      const { deleteEdgeIds, edge } = action.payload;
      for (const id of deleteEdgeIds) {
        deleteEdgeHelper(state, id);
      }

      state.dataDependencyEdges.push(edge);
    },
    createTaskDependency: (
      state,
      action: PayloadAction<Omit<TaskDependency, 'id'>>
    ) => {
      const id = uuid();
      state.taskDependencies[id] = { ...action.payload, id };
    },
    createTimeConstraint: (
      state,
      action: PayloadAction<
        Omit<
          TimeConstraintInstance,
          | 'id'
          | 'minDuration'
          | 'maxDuration'
          | 'enableDetailedCoverageCalculation'
          | 'autoIgnoreIfBroken'
        > & {
          id?: string;
          enableDetailedCoverageCalculation?: boolean;
          autoIgnoreIfBroken?: boolean;
        }
      >
    ) => {
      // Create a temporary time constraint with placeholder ID for checking existence
      const tempTimeConstraint = {
        ...action.payload,
        id: action.payload.id ?? 'temp',
        minDuration: null,
        maxDuration: null,
        enableDetailedCoverageCalculation:
          action.payload.enableDetailedCoverageCalculation ?? true,
        autoIgnoreIfBroken: action.payload.autoIgnoreIfBroken ?? false,
      } satisfies TimeConstraintInstance;

      if (
        timeConstraintConnectionExists(
          getHashedTimeConstraintsByIdMemoized(state.timeConstraints),
          tempTimeConstraint
        )
      ) {
        logger.warn(
          'Time constraint already exists between the two handles, ignoring the createTimeConstraint.',
          tempTimeConstraint
        );
        return;
      }

      const graph = graphologyGraph(
        state.labwareNodes,
        state.taskNodes,
        state.labwareDependencyEdges
      );

      // Rectify the direction first, then generate the ID based on the final values
      const rectifiedTimeConstraint = rectifyTimeConstraintDirection(
        graph,
        tempTimeConstraint
      );

      const id =
        action.payload.id ??
        generateTimeConstraintId(
          rectifiedTimeConstraint.source,
          rectifiedTimeConstraint.target
        );

      // Update with the final ID
      state.timeConstraints[id] = {
        ...rectifiedTimeConstraint,
        // now we override the temp id
        id,
        hardConstraints: [],
        timingTargets: [],
      } as TimeConstraintInstance;
    },
    editTimeConstraintConnection: (
      state,
      action: PayloadAction<{
        patch: Partial<Omit<TimeConstraintInstance, 'id'>>;
        id: string;
      }>
    ) => {
      const { patch, id } = action.payload;

      const timeConstraint = state.timeConstraints[id];
      if (timeConstraint === undefined) {
        logger.error(`Cannot edit time constraint, id "${id}" does not exist`);
        return;
      }

      const patchedTimeConstraint = {
        ...timeConstraint,
        ...patch,
      };

      if (
        timeConstraintConnectionExists(
          getHashedTimeConstraintsByIdMemoized(state.timeConstraints),
          patchedTimeConstraint
        )
      ) {
        logger.warn(
          'Time constraint already exists between the two handles, ignoring the editTimeConstraint.',
          patchedTimeConstraint
        );
      } else {
        const graph = graphologyGraph(
          state.labwareNodes,
          state.taskNodes,
          state.labwareDependencyEdges
        );
        state.timeConstraints[id] = rectifyTimeConstraintDirection(
          graph,
          patchedTimeConstraint
        );
      }
    },
    editTimeConstraintData: (
      state,
      action: PayloadAction<{
        id: string;
        maxDuration?: TimeConstraintInstance['maxDuration'];
        minDuration?: TimeConstraintInstance['minDuration'];
        enableDetailedCoverageCalculation?: TimeConstraintInstance['enableDetailedCoverageCalculation'];
        autoIgnoreIfBroken?: TimeConstraintInstance['autoIgnoreIfBroken'];
      }>
    ) => {
      const { id, ...patch } = action.payload;

      const timeConstraint = state.timeConstraints[id];
      if (timeConstraint === undefined) {
        logger.error(`Cannot edit time constraint, id "${id}" does not exist`);
        return;
      }

      state.timeConstraints[id] = {
        ...timeConstraint,
        ...patch,
      };
    },
    addHardTimeConstraint: (
      state,
      action: PayloadAction<{
        connectionId: string;
        minDuration: number | null;
        maxDuration: number | null;
        enableDetailedCoverageCalculation: boolean;
        autoIgnoreIfBroken: boolean;
      }>
    ) => {
      const {
        connectionId,
        minDuration,
        maxDuration,
        enableDetailedCoverageCalculation,
        autoIgnoreIfBroken,
      } = action.payload;
      const tc = state.timeConstraints[connectionId];
      if (!tc) {
        logger.error(
          `Cannot add hard time constraint, connection ${connectionId} not found`
        );
        return;
      }
      const list = tc.hardConstraints ?? [];
      const nextIndex = list.length + 1;
      const itemId = `${connectionId}_${nextIndex}`;
      state.timeConstraints[connectionId] = {
        ...tc,
        hardConstraints: [
          ...list,
          {
            id: itemId,
            minDuration,
            maxDuration,
            enableDetailedCoverageCalculation,
            autoIgnoreIfBroken,
          },
        ],
      };
    },
    editHardTimeConstraint: (
      state,
      action: PayloadAction<{
        connectionId: string;
        hardConstraintId: string;
        patch: Partial<{
          minDuration: number | null;
          maxDuration: number | null;
          enableDetailedCoverageCalculation: boolean;
          autoIgnoreIfBroken: boolean;
        }>;
      }>
    ) => {
      const { connectionId, hardConstraintId, patch } = action.payload;
      const tc = state.timeConstraints[connectionId];
      if (!tc || !tc.hardConstraints) {
        logger.error(
          `Cannot edit hard time constraint, connection ${connectionId} not found`
        );
        return;
      }
      state.timeConstraints[connectionId] = {
        ...tc,
        hardConstraints: tc.hardConstraints.map((h) =>
          h.id === hardConstraintId ? { ...h, ...patch } : h
        ),
      };
    },
    deleteHardTimeConstraint: (
      state,
      action: PayloadAction<{ connectionId: string; hardConstraintId: string }>
    ) => {
      const { connectionId, hardConstraintId } = action.payload;
      const tc = state.timeConstraints[connectionId];
      if (!tc || !tc.hardConstraints) return;
      state.timeConstraints[connectionId] = {
        ...tc,
        hardConstraints: tc.hardConstraints.filter(
          (h) => h.id !== hardConstraintId
        ),
      };
    },
    addConsistencyTarget: (
      state,
      action: PayloadAction<{
        connectionId: string;
        id?: string;
        groupId?: string;
      }>
    ) => {
      const { connectionId, id, groupId } = action.payload;
      const tc = state.timeConstraints[connectionId];
      if (!tc) {
        logger.error(
          `Cannot add consistency target, connection ${connectionId} not found`
        );
        return;
      }
      const ctId = id ?? uuid();
      const grpId = groupId ?? uuid();
      const list = tc.consistencyTargets ?? [];
      state.timeConstraints[connectionId] = {
        ...tc,
        consistencyTargets: [...list, { id: ctId, groupId: grpId }],
      };
    },
    deleteConsistencyTarget: (
      state,
      action: PayloadAction<{
        connectionId: string;
        consistencyTargetId: string;
      }>
    ) => {
      const { connectionId, consistencyTargetId } = action.payload;
      const tc = state.timeConstraints[connectionId];
      if (!tc || !tc.consistencyTargets) return;
      state.timeConstraints[connectionId] = {
        ...tc,
        consistencyTargets: tc.consistencyTargets.filter(
          (c) => c.id !== consistencyTargetId
        ),
      };
    },
    addTimingTarget: (
      state,
      action: PayloadAction<{
        connectionId: string;
        target: number;
        weight?: number;
        id?: string;
      }>
    ) => {
      const { connectionId, target, weight = 1, id } = action.payload;
      const tc = state.timeConstraints[connectionId];
      if (!tc) {
        logger.error(
          `Cannot add timing target, connection ${connectionId} not found`
        );
        return;
      }
      const ttId = id ?? uuid();
      const list = tc.timingTargets ?? [];
      state.timeConstraints[connectionId] = {
        ...tc,
        timingTargets: [...list, { id: ttId, target, weight }],
      };
    },
    editTimingTarget: (
      state,
      action: PayloadAction<{
        connectionId: string;
        timingTargetId: string;
        patch: { target?: number; weight?: number };
      }>
    ) => {
      const { connectionId, timingTargetId, patch } = action.payload;
      const tc = state.timeConstraints[connectionId];
      if (!tc || !tc.timingTargets) {
        logger.error(
          `Cannot edit timing target, connection ${connectionId} not found`
        );
        return;
      }
      state.timeConstraints[connectionId] = {
        ...tc,
        timingTargets: tc.timingTargets.map((t) =>
          t.id === timingTargetId ? { ...t, ...patch } : t
        ),
      };
    },
    deleteTimingTarget: (
      state,
      action: PayloadAction<{ connectionId: string; timingTargetId: string }>
    ) => {
      const { connectionId, timingTargetId } = action.payload;
      const tc = state.timeConstraints[connectionId];
      if (!tc || !tc.timingTargets) return;
      state.timeConstraints[connectionId] = {
        ...tc,
        timingTargets: tc.timingTargets.filter((t) => t.id !== timingTargetId),
      };
    },
    deleteTaskDependency: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const taskDependency = state.taskDependencies[id];
      if (!taskDependency) {
        logger.error(`Task dependency with id ${id} does not exist`);
      }

      delete state.taskDependencies[id];
    },
    setNodeColorIndex: (
      state,
      action: PayloadAction<{ id: string; colorIndex: number }>
    ) => {
      const { id, colorIndex } = action.payload;
      state.nodeIdToColorIndex[id] = colorIndex;
    },
    updateNodesEdgesSelection: (
      state,
      action: PayloadAction<{
        nodes?: SelectionUpdate;
        edges?: SelectionUpdate;
      }>
    ) => {
      const update = action.payload;

      if (update.nodes !== undefined) {
        state.selectedNodes = mergeSelectionUpdate(
          update.nodes,
          state.selectedNodes
        );
      }

      if (update.edges !== undefined) {
        state.selectedEdges = mergeSelectionUpdate(
          update.edges,
          state.selectedEdges
        );
      }
    },
    selectAllNodesAndEdges: (state) => {
      state.selectedNodes = [
        ...Object.keys(state.labwareNodes),
        ...Object.keys(state.taskNodes),
      ];
      state.selectedEdges = state.labwareDependencyEdges.map(({ id }) => id);
    },
    updateLabwareSlotForTask: (
      state,
      action: PayloadAction<{ taskId: string; labwareId: string; slot: number }>
    ) => {
      const { taskId, labwareId, slot } = action.payload;
      const mappingForTask = state.labwareSlotMappingByTask[taskId] ?? {};

      const fromSlot = mappingForTask[labwareId];

      const labwareIdInTargetSlot = findKey(
        mappingForTask,
        (occupiedSlot) => occupiedSlot === slot
      );
      if (labwareIdInTargetSlot !== undefined && fromSlot !== undefined) {
        // swap with target
        mappingForTask[labwareIdInTargetSlot] = fromSlot;
      }

      mappingForTask[labwareId] = slot;

      state.labwareSlotMappingByTask[taskId] = mappingForTask;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createLabware, (state, action) => {
      createLabwareHelper(state, action.payload);

      const { id } = action.payload.properties;

      // auto-select
      deselectAllNodesEdgesHelper(state);
      state.selectedNodes = [id];
    });

    builder.addCase(deleteNodesEdges, (state, action) => {
      const edgeIds = action.payload.edgeIds;

      if (edgeIds !== undefined) {
        for (const edgeId of edgeIds) {
          deleteAssociatedTaskDependencies(state, edgeId);
          deleteEdgeHelper(state, edgeId);
        }
      }

      deleteNodesHelper({
        state,
        labwareIds: action.payload.labwareIds ?? [],
        taskIds: action.payload.taskIds ?? [],
      });

      state.timeConstraints = {
        ...state.timeConstraints,
        ...rectifyTimeConstraints(
          state.taskNodes,
          state.labwareNodes,
          state.timeConstraints,
          state.labwareDependencyEdges
        ),
      };

      deselectAllNodesEdgesHelper(state);
    });

    builder.addCase(createTask, (state, action) => {
      createTaskHelper(state, action.payload);

      const { id } = action.payload.properties;

      // auto-select
      deselectAllNodesEdgesHelper(state);
      state.selectedNodes = [id];
    });

    builder.addCase(createNodesAndEdges, (state, action) => {
      const {
        taskNodes,
        labwareNodes,
        edges,
        timeConstraints,
        dataDependencyEdges,
      } = action.payload;

      for (const labware of labwareNodes) {
        createLabwareHelper(state, labware);
      }

      for (const task of taskNodes) {
        createTaskHelper(state, task);
      }

      for (const timeConstraint of timeConstraints) {
        state.timeConstraints[timeConstraint.id] = timeConstraint;
      }

      // TODO(IL, 2024-04-19): we might want to ensure that handle IDs are globally unique
      state.labwareDependencyEdges = [
        ...state.labwareDependencyEdges,
        ...edges,
      ];

      state.dataDependencyEdges = [
        ...state.dataDependencyEdges,
        ...dataDependencyEdges,
      ];

      state.selectedNodes = [
        ...labwareNodes.map((node) => node.properties.id),
        ...taskNodes.map((node) => node.properties.id),
      ];
      state.selectedEdges = [
        ...edges.map((edge) => edge.id),
        ...dataDependencyEdges.map((edge) => edge.id),
      ];
    });

    builder.addCase(cutNodeToClipboard, (state, action) => {
      let labwareIds: string[] = [];
      let taskIds: string[] = [];

      switch (action.payload.dataType) {
        case 'labware':
          labwareIds = [action.payload.data.properties.id];
          break;
        case 'task':
          taskIds = [action.payload.data.properties.id];
          break;
        case 'multi':
          labwareIds = action.payload.labwareNodes.map(
            (node) => node.properties.id
          );
          taskIds = action.payload.taskNodes.map((node) => node.properties.id);

          break;
        default:
          action.payload satisfies never;
      }

      deleteNodesHelper({ state, labwareIds, taskIds });
    });

    builder.addCase(loadExistingWorkflow, (_state, action) => {
      return action.payload.visualBuilderSaved.graph;
    });

    builder.addCase(updateTaskParameters, (state, action) => {
      if (!action.payload.isDataOutput) {
        return;
      }

      const handles = Object.values(action.payload.updates)[0];

      if (!Array.isArray(handles)) {
        return;
      }

      state.dataDependencyEdges = state.dataDependencyEdges.filter((edge) => {
        if (edge.source !== action.payload.id) {
          return true;
        }

        return handles.some((handle) => edge.sourceHandle?.includes(handle));
      });
    });
  },
});

export const {
  moveNodes,
  createLabwareDependencyEdge,
  createDataDependencyEdge,
  createTaskDependency,
  createTimeConstraint,
  editTimeConstraintConnection,
  editTimeConstraintData,
  addHardTimeConstraint,
  editHardTimeConstraint,
  deleteHardTimeConstraint,
  addConsistencyTarget,
  deleteConsistencyTarget,
  addTimingTarget,
  editTimingTarget,
  deleteTimingTarget,
  deleteTaskDependency,
  deleteEdges,
  deselectNodesEdges,
  setNodeColorIndex,
  updateNodesEdgesSelection,
  updateLabwareSlotForTask,
  selectAllNodesAndEdges,
} = graphSlice.actions;

export default graphSlice.reducer;
